import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { leadService } from '../../../services/leadService';
import { supabase } from '../../../lib/supabase';

const LeadManagement = () => {
  const { user, userProfile } = useAuth();
  const [filterTag, setFilterTag] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weeklyCount, setWeeklyCount] = useState(0);
  const [courseStats, setCourseStats] = useState([]);
  const [instituteId, setInstituteId] = useState(null);

  useEffect(() => {
    if (!user || !userProfile) return;

    const loadLeads = async () => {
      try {
        if (userProfile?.role !== 'institute') {
          setError('Access denied. This page is for institutes only.');
          setLoading(false);
          return;
        }

        const { data: instituteProfile, error: profileError } = await supabase
          ?.from('institute_profiles')
          ?.select('id')
          ?.eq('user_id', user?.id)
          ?.maybeSingle();

        if (profileError && profileError?.code !== 'PGRST116') {
          throw profileError;
        }

        if (!instituteProfile) {
          setError('Institute profile not found. Please complete your institute registration.');
          setLoading(false);
          return;
        }

        setInstituteId(instituteProfile?.id);

        // Load leads, weekly count, and course stats in parallel
        const [data, weekly, courses] = await Promise.all([
          leadService?.getLeads(instituteProfile?.id, {
            tag: filterTag,
            source: filterSource,
            search: searchQuery
          }),
          leadService?.getLeadsThisWeek(instituteProfile?.id),
          leadService?.getCourseInterestStats(instituteProfile?.id)
        ]);

        setLeads(data || []);
        setWeeklyCount(weekly || 0);
        setCourseStats(courses || []);
      } catch (err) {
        console.error('Error loading leads:', err);
        if (err?.code === 'PGRST116') {
          setError('Institute profile not found. Please complete your institute registration.');
        } else {
          setError(err?.message || 'Failed to load leads');
        }
      } finally {
        setLoading(false);
      }
    };

    loadLeads();
  }, [user, userProfile, filterTag, filterSource, searchQuery]);

  const tagOptions = [
    { value: 'all', label: 'All Leads' },
    { value: 'hot', label: 'Hot Leads' },
    { value: 'warm', label: 'Warm Leads' },
    { value: 'cold', label: 'Cold Leads' }
  ];

  const sourceOptions = [
    { value: 'all', label: 'All Sources' },
    { value: 'booth_visit', label: 'Booth Visit' },
    { value: 'apply_now', label: 'Apply Now' },
    { value: 'chat', label: 'AI Chat' },
    { value: 'video_session', label: 'Video Session' }
  ];

  const getTagColor = (tag) => {
    switch (tag) {
      case 'hot': return 'bg-error text-error-foreground';
      case 'warm': return 'bg-warning text-warning-foreground';
      case 'cold': return 'bg-secondary text-secondary-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getTagLabel = (tag) => {
    switch (tag) {
      case 'hot': return 'Hot';
      case 'warm': return 'Warm';
      case 'cold': return 'Cold';
      default: return 'Lead';
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'booth_visit': return 'Booth Visit';
      case 'apply_now': return 'Apply Now';
      case 'chat': return 'AI Chat';
      case 'video_session': return 'Video Session';
      case 'presentation': return 'Presentation';
      default: return source || 'Direct';
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'booth_visit': return 'MapPin';
      case 'apply_now': return 'FileText';
      case 'chat': return 'MessageSquare';
      case 'video_session': return 'Video';
      default: return 'User';
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'booth_visit': return 'text-blue-600 bg-blue-50';
      case 'apply_now': return 'text-green-600 bg-green-50';
      case 'chat': return 'text-purple-600 bg-purple-50';
      case 'video_session': return 'text-orange-600 bg-orange-50';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads((prev) =>
      prev?.includes(leadId) ? prev?.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads?.length === leads?.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads?.map((lead) => lead?.id));
    }
  };

  const handleExportLeads = () => {
    const leadsToExport = selectedLeads?.length > 0
      ? leads?.filter(lead => selectedLeads?.includes(lead?.id))
      : leads;

    const headers = ['Name', 'Email', 'Phone', 'City', 'State', 'Course Interest', 'Source', 'Status', 'Consent Given', 'Date'];
    const csvContent = [
      headers?.join(','),
      ...leadsToExport?.map(lead => [
        lead?.student?.name || '',
        lead?.consentGiven ? (lead?.student?.email || '') : '[Consent Required]',
        lead?.consentGiven ? (lead?.student?.phone || '') : '[Consent Required]',
        lead?.student?.city || '',
        lead?.student?.state || '',
        lead?.courseInterest || '',
        getSourceLabel(lead?.source),
        getTagLabel(lead?.leadStatus),
        lead?.consentGiven ? 'Yes' : 'No',
        lead?.createdAt ? new Date(lead?.createdAt)?.toLocaleDateString() : ''
      ]?.map(field => `"${String(field)?.replace(/"/g, '""')}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `leads_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading leads...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <p className="text-error mb-4">{error}</p>
        <button onClick={() => window.location?.reload()} className="text-primary hover:underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground data-text">{weeklyCount}</p>
            <p className="text-xs text-muted-foreground">Leads this week</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Users" size={20} color="var(--color-success)" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground data-text">{leads?.length}</p>
            <p className="text-xs text-muted-foreground">Total leads</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="BookOpen" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground data-text">{courseStats?.[0]?.course || '—'}</p>
            <p className="text-xs text-muted-foreground">Top course interest</p>
          </div>
        </div>
      </div>

      {/* Course Interest Breakdown */}
      {courseStats?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="BarChart2" size={16} color="var(--color-primary)" />
            Course Interest Breakdown
          </h3>
          <div className="flex flex-wrap gap-2">
            {courseStats?.slice(0, 8)?.map((stat, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-full">
                <span className="text-sm text-foreground font-medium">{stat?.course}</span>
                <span className="text-xs text-muted-foreground bg-background px-1.5 py-0.5 rounded-full">{stat?.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Leads Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="border-b border-border p-4 md:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Target" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                  Lead Management
                </h2>
                <p className="text-sm text-muted-foreground">
                  <span className="data-text">{leads?.length}</span> leads found
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="w-full sm:w-40">
                <Select
                  options={sourceOptions}
                  value={filterSource}
                  onChange={setFilterSource}
                  placeholder="Filter by source"
                />
              </div>
              <div className="w-full sm:w-40">
                <Select
                  options={tagOptions}
                  value={filterTag}
                  onChange={setFilterTag}
                  placeholder="Filter by tag"
                />
              </div>
              <div className="w-full sm:w-56">
                <Input
                  type="search"
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                />
              </div>
            </div>
          </div>

          {selectedLeads?.length > 0 && (
            <div className="mt-4 flex items-center justify-between p-3 bg-primary/10 rounded-lg">
              <span className="text-sm font-medium text-primary">
                <span className="data-text">{selectedLeads?.length}</span> leads selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExportLeads}
                >
                  Export Selected
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedLeads([])}>
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>

        {leads?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No leads yet</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Leads are automatically captured when students visit your booth, click Apply Now, start a chat, or book a video session.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedLeads?.length === leads?.length && leads?.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-border"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Student</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Contact</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Course Interest</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Source</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Consent</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {leads?.map((lead) => (
                  <tr key={lead?.id} className="hover:bg-muted/50 transition-smooth">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedLeads?.includes(lead?.id)}
                        onChange={() => handleSelectLead(lead?.id)}
                        className="w-4 h-4 rounded border-border"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <Image
                          src="https://img.rocket.new/generatedImages/rocket_gen_img_1c6a0feff-1763294815529.png"
                          alt={`${lead?.student?.name || 'Student'} profile photo`}
                          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate text-sm">{lead?.student?.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {[lead?.student?.city, lead?.student?.state]?.filter(Boolean)?.join(', ') || 'Location N/A'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {lead?.consentGiven ? (
                        <div className="min-w-0">
                          <p className="text-sm text-foreground truncate">{lead?.student?.email || 'N/A'}</p>
                          <p className="text-xs text-muted-foreground truncate">{lead?.student?.phone || 'N/A'}</p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <Icon name="Lock" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-xs text-muted-foreground">Consent pending</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-foreground">
                        {lead?.courseInterest || <span className="text-muted-foreground text-xs">Not specified</span>}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getSourceColor(lead?.source)}`}>
                        <Icon name={getSourceIcon(lead?.source)} size={12} />
                        {getSourceLabel(lead?.source)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getTagColor(lead?.leadStatus)}`}>
                        {getTagLabel(lead?.leadStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {lead?.consentGiven ? (
                        <div className="flex items-center gap-1.5">
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                          <span className="text-xs text-success font-medium">Given</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-xs text-muted-foreground">Pending</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-foreground">
                        {lead?.createdAt ? new Date(lead?.createdAt)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="border-t border-border p-4 md:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Showing <span className="data-text">{leads?.length}</span> leads
            </p>
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={handleExportLeads}
              disabled={leads?.length === 0}
            >
              Export All Leads (CSV)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadManagement;